import { defineStore } from "pinia";

import { useUserStore } from "./user";

import { setKey as setBrowserStorageKey } from "./helpers/localStorage.mjs";
import { fetchFromBackend } from "./helpers/backend.mjs";
import { useNotificationStore } from './notification';

export const useActivityStore = defineStore("activity", {
  state: () => {
    return {
      activities: [],
    }
  },
  getters: {
    // return the full activity data, the signature allows for finding the activity by display (i.e. week or month) and campaign.id
    activity: (state) => {
      if (!state.activities) return false;
      const userStore = useUserStore();
      let activity = null;
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          activity = state.activities.find((activity) => activity.signature === `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'salesTeam':
          activity = state.activities.find((activity) => activity.signature === `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'campaign':
          activity = state.activities.find((activity) => activity.signature === `campaign:${userStore.currentSelection.campaign.id}`);
          break;
      }
      if (!activity) return false;
      return activity.data;
    },
    week: (state) => {
      // console.log('getting week data')
      const userStore = useUserStore();
      let activity = null;
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          activity = state.activities.find((activity) => activity.signature === `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'salesTeam':
          activity = state.activities.find((activity) => activity.signature === `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'campaign':
          activity = state.activities.find((activity) => activity.signature === `campaign:${userStore.currentSelection.campaign.id}`);
          break;
      }
      // console.log('activity2')
      if (!activity) return false;
      // console.log('false')
      return activity.data.week.find((unit) => unit.id === userStore.currentSelection.activity.week);
    },
    month: (state) => {
      const userStore = useUserStore();
      let activity = null;
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          activity = state.activities.find((activity) => activity.signature === `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'salesTeam':
          activity = state.activities.find((activity) => activity.signature === `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'campaign':
          activity = state.activities.find((activity) => activity.signature === `campaign:${userStore.currentSelection.campaign.id}`);
          break;
      }
      if (!activity) return false;
      return activity.data.month.find((unit) => unit.id === userStore.currentSelection.activity.month);
    },
    halftime: (state) => {
      const userStore = useUserStore();
      let activity = null;
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          activity = state.activities.find((activity) => activity.signature === `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'salesTeam':
          activity = state.activities.find((activity) => activity.signature === `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'campaign':
          activity = state.activities.find((activity) => activity.signature === `campaign:${userStore.currentSelection.campaign.id}`);
          break;
      }
      if (!activity) return false;
      return activity.data['periodEnd:ht'][0];
    },
    campaign: (state) => {
      const userStore = useUserStore();
      let activity = null;
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          activity = state.activities.find((activity) => activity.signature === `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'salesTeam':
          activity = state.activities.find((activity) => activity.signature === `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`);
          break;
        case 'campaign':
          activity = state.activities.find((activity) => activity.signature === `campaign:${userStore.currentSelection.campaign.id}`);
          break;
      }
      if (!activity) return false;
      return activity.data['periodEnd'][0];
    }
  },
  actions: {
    /** see if the incentives need to be fetched again, either because the data is not up to date or the display status has changed
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      
      // create a signature to see if the data is is up to date or needs to be fetched
      let signature = '';
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          signature = `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;
          break;
        case 'salesTeam':
          signature = `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`;
          break;
        case 'campaign':
          signature = `campaign:${userStore.currentSelection.campaign.id}`;
          break;
      }
      // console.log('refreshing all stores with scope', userStore.currentSelection.scope, signature)

      const activityExists = this.activities.find((activity) => activity.signature === signature);
      // return the existing data if it exists and its update property is less then 6 hours old
      if (activityExists && activityExists?.update > (Date.now() - (6 * 60 * 60 * 1000))) return activityExists;
      // fetch the data from the backend, get both halftime and campaign data
      const taskList = [
        fetchFromBackend("activityMonitor/v1/salesAgents", {
          id: userStore.id,
          filter: {
            mode: 'week',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
        fetchFromBackend("activityMonitor/v1/salesAgents", {
          id: userStore.id,
          filter: {
            mode: 'month',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
        fetchFromBackend("activityMonitor/v1/salesAgents", {
          id: userStore.id,
          filter: {
            mode: 'periodEnd:ht',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
        fetchFromBackend("activityMonitor/v1/salesAgents", {
          id: userStore.id,
          filter: {
            mode: 'period',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
      ];
      try {
        userStore.currentSelection.activity.isLoading = true;
        const responses = await Promise.all(taskList)
        userStore.currentSelection.activity.isLoading = false;

        const activityData = {};
        const segments = ['week', 'month', 'periodEnd:ht', 'periodEnd'];
        for (const [index, segment] of segments.entries()) {
          const resp = responses[index] || {};
            const units = Array.isArray(resp.units) ? resp.units : [];
            activityData[segment] = units;
            // meta assignment with guards
            if (segment === 'week') {
              activityData.idCurrentWeek = resp.currentUnit ?? null;
            }
            if (segment === 'month') {
              activityData.levelList = Array.isArray(resp.levelList) ? resp.levelList : [];
              activityData.idCurrentMonth = resp.currentUnit ?? null;
            }
            if (units.length > 0 && resp.currentUnit !== undefined && resp.currentUnit !== null) {
              for (const unit of units) {
                if (resp.currentUnit == unit.id) {
                  unit.isCurrent = true;
                  userStore.currentSelection.activity[segment] = unit.id;
                  break;
                }
              }
            }
        }
        if (activityExists) {
          Object.assign(activityExists, { update: Date.now(), data: activityData });
        } else {
          this.activities.push({ signature: signature, update: Date.now(), data: activityData });
        }
        // notificationStore.addNotification('Aktivitätsdaten erfolgreich geladen!', 'success');
      } catch (error) {
        userStore.currentSelection.activity.isLoading = false;
        notificationStore.addNotification('Aktivitätsdaten konnten nicht geladen werden!', 'error', 1);
        throw error;
      }
    },
    /** see if the incentives need to be fetched again, either because the data is not up to date or the display status has changed
    */
    async refreshMode(mode) {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // create a signature to see if the data is is up to date or needs to be fetched
      let signature = '';
      switch (userStore.currentSelection.scope) {
        case 'salesAgent':
          signature = `salesAgent:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;
          break;
        case 'salesTeam':
          signature = `salesTeam:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`;
          break;
        case 'campaign':
          signature = `campaign:${userStore.currentSelection.campaign.id}`;
          break;
      }
      const activityExists = this.activities.find((activity) => activity.signature === signature);
      if (!activityExists) return false;
      // fetch the data from the backend, get both halftime and campaign data
      try {
        userStore.currentSelection.activity.isLoading = true;
        const response = await fetchFromBackend("activityMonitor/v1/salesAgents", {
          id: userStore.id,
          filter: {
            mode: mode,
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        });
        userStore.currentSelection.activity.isLoading = false;

        activityExists.data[mode] = response.units;
        if (mode === 'month') activityExists.data[`levelList`] = response.levelList;
        for (const unit of activityExists.data[mode]) {
          if (response.currentUnit == unit.id) {
            unit.isCurrent = true;
            userStore.currentSelection.activity[mode] = unit.id;
          }
        }
        // notificationStore.addNotification('Aktivitätsdaten erfolgreich geladen!', 'success');
      } catch (error) {
        userStore.currentSelection.activity.isLoading = false;
        notificationStore.addNotification('Aktivitätsdaten konnten nicht geladen werden!', 'error', 1);
        throw error;
      }
    },
  },
});
